import MixedLink from 'common/components/MixedLink';
import ThemeSwitcher from 'common/components/ThemeSwitcher/ThemeSwitcher';
import { graphql, useStaticQuery } from 'gatsby';
import { OutboundLink } from 'gatsby-plugin-google-gtag';
import React, { useEffect } from 'react';
import { usePopper } from 'react-popper';
import FullHr from '../FullHr';
import { sidebarItems } from 'constants/constants';
import { useDarkMode } from 'hooks/useDarkMode';
import { MINTABLE_TOKENS } from 'constants/contracts';
import useSelectedChain from 'hooks/useSelectedChain';

type Props = {
  toggleTheme?: () => void;
};

const RightNav: React.FC<Props> = (props) => {
  const [isActive, setIsActive] = React.useState(false);
  const [referenceElement, setReferenceElement] = React.useState<unknown>(null);
  const [popperElement, setPopperElement] = React.useState<unknown>(null);
  const { chainId } = useSelectedChain();
  const hasMint = !!MINTABLE_TOKENS[chainId];
  let items = sidebarItems.filter((i) => i.position === 'bottom');
  if (!hasMint) {
    items = items.filter((i) => i.path !== '/mint' && i.path !== '/treasury');
  }

  const {
    styles,
    attributes,
    update: popperUpdate,
  } = usePopper(referenceElement as Element, popperElement as HTMLElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ],
  });

  useEffect(() => {
    // close popper when clicked out
    const handleOnClick = (ev) => {
      if (
        !(ev.composedPath() as Array<HTMLElement>).includes(
          popperElement as HTMLElement
        ) &&
        !(ev.composedPath() as Array<HTMLElement>).includes(
          referenceElement as HTMLElement
        )
      ) {
        setIsActive(false);
      }
    };
    document.addEventListener('click', handleOnClick);
    return () => document.removeEventListener('click', handleOnClick);
  });

  const setPopperIsActive = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    val: boolean
  ) => {
    event.preventDefault();
    if (popperUpdate) {
      void popperUpdate();
    }
    setIsActive(val);
  };

  const handleThemeSwitch = () => {
    if (props.toggleTheme) props.toggleTheme();
    setIsActive(false);
  };

  const Data = useStaticQuery(graphql`
    query {
      ichiJson {
        QUANT_STAMP {
          url
        }
      }
    }
  `);
  const quantStampURL = Data.ichiJson.QUANT_STAMP.url;

  const darkMode = useDarkMode();
  const switchMode = darkMode.isDarkMode ? 'Light Mode' : 'Dark Mode';
  return (
    <>
      <button
        id="nav-button"
        className="h-10 w-10 rounded-[10px] ml-3 mr-3 text-2xl sub-text-color bold nav-button-bg-color"
        ref={setReferenceElement}
        onClick={(event) => setPopperIsActive(event, !isActive)}
      >
        ⋮
      </button>
      <div
        id="right-nav"
        className={`ichi-balance-bg-color z-[5000] p-3 pb-24 tablet:pb-3 rounded-[10px]
          absolute right-full shadow-[0_2px_50px_0_rgba(0,0,0,0.25)]
          ${isActive ? 'inline' : 'hidden'}
        `}
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
      >
        <div className="flex flex-col p-2 w-full">
          {items.map((i) => (
            <MixedLink
              key={i.label}
              href={i.path}
              handleClick={() => setIsActive(false)}
              className="text-base font-medium primary-text-color"
            >
              <div className="flex flex-row items-center h-11">
                <div className="flex flex-row items-center justify-center w-[30px] mr-4">
                  <img src={i.icon.light.default.publicURL} />
                </div>
                {i.label}
              </div>
            </MixedLink>
          ))}
          <FullHr />
          <OutboundLink
            target="_blank"
            href={quantStampURL}
            className="text-base font-medium primary-text-color"
          >
            <div className="flex flex-row items-center h-11">
              <img alt="Quantstamp" className="icon-quantstamp w-[30px] mr-4" />
              Quantstamp Verified
            </div>
          </OutboundLink>
          <FullHr />
          <div
            className="flex flex-row items-center h-11 text-base font-medium primary-text-color cursor-pointer"
            onClick={handleThemeSwitch}
          >
            <ThemeSwitcher />
            {switchMode}
          </div>
        </div>
      </div>
    </>
  );
};

export default RightNav;
